import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck, Check, X, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Tag, Spinner, EmptyState } from "@/components/shared";

function Photo({ src, label }) {
  const url = fileUrl(src);
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  const showImg = url && !broken;
  return (
    <div className="flex-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</div>
      <div className="aspect-square rounded-xl overflow-hidden bg-[hsl(37_20%_88%)] ring-1 ring-border/60 grid place-items-center">
        {showImg ? (
          <img
            src={url}
            alt={label}
            className="h-full w-full object-cover"
            onError={() => setBroken(true)}
          />
        ) : (
          <div className="flex flex-col items-center text-[hsl(189_20%_35%)]">
            <ImageOff className="h-7 w-7" />
            <span className="text-xs mt-1">No image</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Verifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.verifications();
      setItems(res.verifications || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = items.filter((v) => (tab === "pending" ? !v.reviewed : v.reviewed));
  const pendingCount = items.filter((v) => !v.reviewed).length;

  const review = async (vid, approve) => {
    try {
      await api.reviewVerification(vid, approve);
      toast.success(approve ? "Verification approved" : "Verification rejected");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <PageHeader title="Verifications" subtitle={`${pendingCount} awaiting review`} />

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList data-testid="verif-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({pendingCount})</TabsTrigger>
          <TabsTrigger value="reviewed" data-testid="tab-reviewed">Reviewed</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyState icon={ShieldCheck} title={tab === "pending" ? "No pending verifications" : "No reviewed verifications"} hint="Selfie checks will appear here." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="verif-grid">
          {filtered.map((v) => (
            <div
              key={v.verification_id}
              className="rounded-2xl bg-card ring-1 ring-border/70 p-4"
              data-testid={`verif-${v.verification_id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{v.user_name || "Unknown user"}</div>
                  <div className="text-xs text-muted-foreground truncate">{v.user_id}</div>
                </div>
                <div className="flex gap-1.5">
                  {v.verdict && (
                    <Tag tone={v.verdict === "MATCH" ? "green" : "red"}>{v.verdict}</Tag>
                  )}
                  {v.user_verified && <Tag tone="green">Verified</Tag>}
                </div>
              </div>

              <div className="flex gap-3">
                <Photo src={v.selfie_url} label="Selfie" />
                <Photo src={v.profile_photo} label="Profile" />
              </div>

              {v.reviewed ? (
                <div className="mt-3 text-center">
                  <Tag tone={v.result ? "green" : "red"}>
                    {v.result ? "Approved" : "Rejected"}
                  </Tag>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => review(v.verification_id, false)}
                    data-testid={`reject-${v.verification_id}`}
                  >
                    <X className="h-4 w-4" /> Reject
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                    onClick={() => review(v.verification_id, true)}
                    data-testid={`approve-${v.verification_id}`}
                  >
                    <Check className="h-4 w-4" /> Approve
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
